import type {
  LocalProjectionPayload,
  LoyaltyRewardCatalogRuntimeItem,
  LoyaltyRuntimeItem,
} from "../gateway/local-projection-client";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

function loyaltyScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:loyalty`;
}

function runtimeKey(identity: TrustedTerminalIdentity, projectionKind: string) {
  return `${loyaltyScope(identity)}:${projectionKind}`;
}

function revisionFor(item: LoyaltyRuntimeItem | LoyaltyRewardCatalogRuntimeItem) {
  return item.projection_kind === "BASE_EARN_POLICY"
    ? `${item.policy_version_id}:${item.changed_at}`
    : `reward-catalog:${item.changed_at}`;
}

export async function applyLoyaltyRuntime(
  identity: TrustedTerminalIdentity,
  payload: LocalProjectionPayload<"LOYALTY">,
) {
  const scopeKey = loyaltyScope(identity);
  await posRuntimeDb.transaction("rw", posRuntimeDb.loyaltyRuntime, async () => {
    if (payload.mode === "FULL" || payload.mode === "DISABLED") {
      await posRuntimeDb.loyaltyRuntime.where("scopeKey").equals(scopeKey).delete();
    }
    if (payload.mode === "DISABLED") return;
    for (const item of payload.items) {
      await posRuntimeDb.loyaltyRuntime.put({
        key: runtimeKey(identity, item.projection_kind),
        scopeKey,
        customerId: item.loyalty_program_id,
        projectionKind: item.projection_kind,
        revision: revisionFor(item),
        updatedAt: item.changed_at,
        payload: item,
      });
    }
  });
}

export async function readPublishedLoyaltyBaseEarnRuntime(
  identity: TrustedTerminalIdentity,
): Promise<LoyaltyRuntimeItem | null> {
  const row = await posRuntimeDb.loyaltyRuntime.get(runtimeKey(identity, "BASE_EARN_POLICY"));
  if (!row?.payload || typeof row.payload !== "object") return null;
  const item = row.payload as Partial<LoyaltyRuntimeItem>;
  if (
    item.projection_kind !== "BASE_EARN_POLICY"
    || typeof item.policy_version_id !== "string"
    || typeof item.policy_version_no !== "number"
    || !item.policy
  ) return null;
  return item as LoyaltyRuntimeItem;
}

export async function readPublishedLoyaltyRewardCatalogRuntime(
  identity: TrustedTerminalIdentity,
): Promise<LoyaltyRewardCatalogRuntimeItem | null> {
  const row = await posRuntimeDb.loyaltyRuntime.get(runtimeKey(identity, "REWARD_CATALOG"));
  if (!row?.payload || typeof row.payload !== "object") return null;
  const item = row.payload as Partial<LoyaltyRewardCatalogRuntimeItem>;
  if (
    item.projection_kind !== "REWARD_CATALOG"
    || !Array.isArray(item.rewards)
    || typeof item.loyalty_program_id !== "string"
  ) return null;
  return item as LoyaltyRewardCatalogRuntimeItem;
}
